import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const inputVariants = cva(
  [
    "ui-control",
    "block w-full rounded-md",
    "bg-white text-cloverhound-gray-900 placeholder:text-cloverhound-gray-600",
    "dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50 dark:placeholder:text-cloverhound-gray-500",
    "border border-cloverhound-gray-300 dark:border-cloverhound-gray-800",
    "transition-colors duration-quick ease-out-quad motion-reduce:transition-none",
    "focus:outline-none focus:border-cloverhound-indigo-400 focus:ring-2 focus:ring-cloverhound-indigo-500/30",
    "disabled:cursor-not-allowed disabled:opacity-70",
    "disabled:bg-cloverhound-gray-100 dark:disabled:bg-cloverhound-gray-950",
  ].join(" "),
  {
    variants: {
      state: {
        default: "",
        error: "border-cloverhound-red-500 dark:border-cloverhound-red-500 focus:border-cloverhound-red-500 focus:ring-cloverhound-red-500/30",
      },
      size: {
        sm: "h-7 px-2 text-mini",
        md: "h-8 px-3 text-small",
        lg: "h-10 px-4 text-regular",
      },
    },
    defaultVariants: { state: "default", size: "md" },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, state, size, leadingIcon, trailingIcon, type = "text", ...props }, ref) => {
    if (leadingIcon || trailingIcon) {
      return (
        <span className={cn("relative inline-block w-full align-middle", className)}>
          {leadingIcon && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-cloverhound-gray-600 [&>*]:size-4"
            >
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            type={type}
            className={cn(
              inputVariants({ state, size }),
              leadingIcon && "pl-8",
              trailingIcon && "pr-8",
            )}
            {...props}
          />
          {trailingIcon && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-cloverhound-gray-600 [&>*]:size-4"
            >
              {trailingIcon}
            </span>
          )}
        </span>
      )
    }
    return (
      <input ref={ref} type={type} className={cn(inputVariants({ state, size }), className)} {...props} />
    )
  },
)
Input.displayName = "Input"
